/**
 * DishDetailDrawer — rich slide-over (desktop) / full-page modal (mobile)
 * for any dish. Triggered from a planner chip or a Recipes-tab card.
 *
 * Data source: GET /api/meals/dishes/<dish_key>?name=<display_name>
 *   - First open of a new dish triggers a one-time AI call (~7-10s).
 *   - Subsequent opens return cached detail in <300ms.
 *
 * Optional actions:
 *   - "Generate alternative" -> POST /api/meals/dishes/<key>/refresh-recipe
 *   - "Generate step photos" -> POST /api/meals/dishes/<key>/step-images
 *
 * YouTube: when a YouTube Data API v3 key is configured in Settings, the top
 * recipe video is auto-embedded. Otherwise we render a "Search on YouTube"
 * button that opens youtube.com/results in a new tab.
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { X, ArrowsClockwise, FilmStrip, ArrowSquareOut, Clock, ForkKnife, Bookmark, Spinner, WhatsappLogo, Link as LinkIcon } from '@phosphor-icons/react';

const absUrl = (api, p) => {
  if (!p) return '';
  if (p.startsWith('http')) return p;
  const base = api?.defaults?.baseURL?.replace(/\/api\/?$/, '') || '';
  return `${base}${p}`;
};

const dishKeyOf = (name) =>
  (name || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9 ]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/ /g, '_')
    .slice(0, 60);

export default function DishDetailDrawer({ api, dishName, shareContext, onClose }) {
  const dishKey = useMemo(() => dishKeyOf(dishName), [dishName]);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(''); // 'refresh' | 'steps' | 'share' | ''
  const [shareLink, setShareLink] = useState('');
  const [shareCopied, setShareCopied] = useState(false);

  const load = useCallback(async () => {
    if (!dishKey) return;
    setLoading(true);
    setError('');
    try {
      const r = await api.get(`/meals/dishes/${dishKey}`, { params: { name: dishName } });
      setDetail(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [api, dishKey, dishName]);

  useEffect(() => { load(); }, [load]);

  // ESC to close
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const refreshRecipe = async () => {
    setBusy('refresh');
    try {
      await api.post(`/meals/dishes/${dishKey}/refresh-recipe`, { name: dishName });
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Refresh failed');
    } finally { setBusy(''); }
  };

  const genStepImages = async () => {
    setBusy('steps');
    try {
      await api.post(`/meals/dishes/${dishKey}/step-images`);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Step image generation failed');
    } finally { setBusy(''); }
  };

  // Build the public link the cook will receive. Origin = current site root.
  // When opened from a planner chip we include plan_id+slot+date so the cook's
  // per-step "Done" taps roll up into a single "meal is ready" notification.
  const buildShareUrl = (lang) => {
    const origin = window.location.origin;
    const qs = new URLSearchParams();
    if (lang === 'hi') qs.set('lang', 'hi');
    if (shareContext?.plan_id) qs.set('plan_id', shareContext.plan_id);
    if (shareContext?.slot) qs.set('slot', shareContext.slot);
    if (shareContext?.date) qs.set('date', shareContext.date);
    const qstr = qs.toString();
    return `${origin}/r/${dishKey}${qstr ? `?${qstr}` : ''}`;
  };

  // Auto-generate Hindi + step images (3a), then open WhatsApp + copy link.
  const shareWithCook = async () => {
    setBusy('share');
    setError('');
    try {
      const r = await api.post(`/meals/dishes/${dishKey}/prepare-share`);
      const lang = r.data?.lang === 'hi' ? 'hi' : 'en';
      const url = buildShareUrl(lang);
      setShareLink(url);

      // Copy to clipboard (best-effort).
      try { await navigator.clipboard.writeText(url); setShareCopied(true); setTimeout(() => setShareCopied(false), 3000); }
      catch { /* ignored — many mobile browsers block clipboard without HTTPS+gesture */ }

      // Open WhatsApp share intent in a new tab/window.
      const msg = lang === 'hi'
        ? `${detail?.name || dishName} — रेसिपी:\n${url}`
        : `${detail?.name || dishName} — recipe:\n${url}`;
      window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank', 'noopener');
      await load(); // refresh drawer state so step photos now show up
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Failed to prepare share link');
    } finally { setBusy(''); }
  };

  const copyShareLink = async () => {
    const lang = window.localStorage.getItem('mealExportPrefs.v1');
    let langCode = 'en';
    try { langCode = (lang && JSON.parse(lang).lang) || 'en'; } catch { /* ignore */ }
    const url = buildShareUrl(langCode);
    setShareLink(url);
    try { await navigator.clipboard.writeText(url); setShareCopied(true); setTimeout(() => setShareCopied(false), 3000); }
    catch { /* ignore */ }
  };

  return (
    <div className="fixed inset-0 z-[80] flex" data-testid="dish-detail-drawer">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* Panel: full-screen on mobile, right-side drawer on desktop */}
      <div className="relative ml-auto w-full md:w-[640px] lg:w-[760px] h-full bg-neutral-950 overflow-y-auto shadow-2xl">
        {/* Sticky header */}
        <div className="sticky top-0 z-10 bg-neutral-950/95 backdrop-blur-md border-b border-white/10 px-5 py-3 flex items-center gap-3">
          <ForkKnife size={22} className="text-[#FF6B35]" />
          <h2 className="text-lg font-semibold text-white flex-1 truncate" data-testid="dish-detail-title">{detail?.name || dishName}</h2>
          <button onClick={onClose} data-testid="dish-detail-close"
            className="p-1.5 rounded-lg text-neutral-400 hover:text-white hover:bg-white/10">
            <X size={20} />
          </button>
        </div>

        {loading && (
          <div className="p-8 flex flex-col items-center gap-3 text-neutral-400" data-testid="dish-detail-loading">
            <Spinner size={32} className="animate-spin text-[#FF6B35]" />
            <p className="text-sm">Cooking up the details… (first time can take ~7s)</p>
          </div>
        )}

        {error && (
          <div className="p-5 m-5 rounded-xl bg-red-950/40 border border-red-800/40 text-red-300 text-sm" data-testid="dish-detail-error">
            {error}
          </div>
        )}

        {!loading && !error && detail && (
          <div className="p-5 space-y-6">
            {/* Hero image */}
            {detail.hero_image_url && (
              <div className="relative rounded-2xl overflow-hidden aspect-video bg-neutral-900">
                <img src={absUrl(api, detail.hero_image_url)} alt={detail.name}
                  className="w-full h-full object-cover" loading="lazy" data-testid="dish-detail-hero" />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                  <div className="flex flex-wrap items-center gap-2 text-xs">
                    {detail.cuisine && <span className="px-2 py-0.5 rounded-full bg-orange-500/20 text-orange-300 border border-orange-500/30">{detail.cuisine}</span>}
                    {detail.diet_type && <span className="px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">{detail.diet_type}</span>}
                    {(detail.prep_time_mins != null || detail.cook_time_mins != null) && (
                      <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/10 text-white/80">
                        <Clock size={12} /> {(detail.prep_time_mins || 0) + (detail.cook_time_mins || 0)} min
                      </span>
                    )}
                    {detail.servings && <span className="px-2 py-0.5 rounded-full bg-white/10 text-white/80">Serves {detail.servings}</span>}
                  </div>
                </div>
              </div>
            )}

            {/* Description */}
            {detail.description && (
              <p className="text-[15px] text-neutral-300 leading-relaxed" data-testid="dish-detail-description">{detail.description}</p>
            )}

            {/* Video */}
            {detail.video && (
              <div className="space-y-2" data-testid="dish-detail-video">
                <h3 className="text-sm uppercase tracking-wider text-neutral-500 font-semibold">Watch & cook</h3>
                {detail.video.embed_url ? (
                  <div className="relative aspect-video rounded-xl overflow-hidden bg-black border border-white/5">
                    <iframe
                      src={detail.video.embed_url}
                      title={detail.video.title}
                      className="absolute inset-0 w-full h-full"
                      allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      data-testid="dish-detail-yt-embed"
                    />
                  </div>
                ) : (
                  <a href={detail.video.search_url} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-red-600/20 hover:bg-red-600/30 text-red-300 border border-red-600/40 text-sm font-semibold transition"
                    data-testid="dish-detail-yt-search">
                    <ArrowSquareOut size={16} /> Search recipes on YouTube
                  </a>
                )}
                {detail.video.title && (
                  <p className="text-xs text-neutral-500">{detail.video.channel ? `${detail.video.channel} · ` : ''}{detail.video.title}</p>
                )}
                {detail.video.fallback && (
                  <p className="text-[11px] text-neutral-600">Tip: add a YouTube Data API key in Settings → API Keys to auto-embed the top recipe.</p>
                )}
              </div>
            )}

            {/* Varieties */}
            {detail.varieties?.length > 0 && (
              <div className="space-y-2" data-testid="dish-detail-varieties">
                <h3 className="text-sm uppercase tracking-wider text-neutral-500 font-semibold">Varieties & forms</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {detail.varieties.map((v, i) => (
                    <div key={i} className="p-3 rounded-xl bg-white/5 border border-white/5">
                      <p className="text-sm font-semibold text-white">{v.name}</p>
                      <p className="text-xs text-neutral-400 mt-0.5">{v.note}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* User recipe (if exists) */}
            {detail.user_recipe && (
              <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4" data-testid="dish-detail-user-recipe">
                <div className="flex items-center gap-2 mb-2">
                  <Bookmark size={16} weight="fill" className="text-emerald-400" />
                  <p className="text-sm font-semibold text-emerald-300">Your saved recipe</p>
                </div>
                <p className="text-xs text-neutral-400">Found a match in your Recipes library — using your version below.</p>
              </div>
            )}

            {/* Ingredients */}
            {(detail.ingredients?.length > 0) && (
              <div className="space-y-2">
                <h3 className="text-sm uppercase tracking-wider text-neutral-500 font-semibold">Ingredients</h3>
                <ul className="text-sm text-neutral-200 space-y-1.5">
                  {detail.ingredients.map((ing, i) => (
                    <li key={i} className="flex items-baseline gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#FF6B35] flex-shrink-0 translate-y-1.5" />
                      <span><b>{ing.quantity}{ing.unit ? ` ${ing.unit}` : ''}</b> {ing.name}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Instructions */}
            {detail.instructions?.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm uppercase tracking-wider text-neutral-500 font-semibold">Method</h3>
                  <button onClick={genStepImages} disabled={busy === 'steps' || detail.step_images?.length > 0}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-purple-500/15 hover:bg-purple-500/25 disabled:opacity-50 text-purple-300 text-xs font-semibold border border-purple-500/30 transition"
                    data-testid="dish-detail-gen-steps">
                    {busy === 'steps' ? <Spinner size={14} className="animate-spin" /> : <FilmStrip size={14} />}
                    {detail.step_images?.length > 0 ? 'Photos generated' : 'Visualize each step'}
                  </button>
                </div>
                <ol className="space-y-3">
                  {detail.instructions.map((step, i) => {
                    const img = detail.step_images?.find(s => s.index === i + 1);
                    return (
                      <li key={i} className="flex gap-3">
                        <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#FF6B35]/15 text-[#FF6B35] text-sm font-bold flex items-center justify-center border border-[#FF6B35]/30">{i + 1}</span>
                        <div className="flex-1 space-y-2">
                          <p className="text-sm text-neutral-200 leading-relaxed">{step}</p>
                          {img && (
                            <img
                              src={absUrl(api, `/api/meals/dish-steps/${dishKey}_step${i + 1}.jpg`)}
                              alt={`Step ${i + 1}`}
                              className="w-full max-w-sm rounded-lg border border-white/5"
                              loading="lazy"
                              data-testid={`dish-detail-step-img-${i + 1}`}
                            />
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ol>
              </div>
            )}

            {/* Tips */}
            {detail.tips?.length > 0 && (
              <div className="rounded-xl bg-amber-500/5 border border-amber-500/20 p-4">
                <h3 className="text-xs uppercase tracking-wider text-amber-400 font-semibold mb-2">Cook&apos;s tips</h3>
                <ul className="text-sm text-amber-100/90 space-y-1.5">
                  {detail.tips.map((tip, i) => (
                    <li key={i} className="flex gap-2"><span>•</span><span>{tip}</span></li>
                  ))}
                </ul>
              </div>
            )}

            {/* Footer actions */}
            <div className="pt-3 border-t border-white/5 space-y-2">
              {/* Share with Cook — primary action */}
              <div className="flex flex-wrap gap-2">
                <button onClick={shareWithCook} disabled={busy === 'share'}
                  className="flex-1 min-w-[200px] inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-[#25D366] hover:bg-[#1ebe5d] disabled:opacity-50 text-white text-sm font-bold transition shadow-lg shadow-[#25D366]/20"
                  data-testid="dish-detail-share-whatsapp">
                  {busy === 'share' ? <Spinner size={16} className="animate-spin" /> : <WhatsappLogo size={18} weight="fill" />}
                  {busy === 'share' ? 'Preparing recipe (~30s)…' : 'Share with Cook (WhatsApp)'}
                </button>
                <button onClick={copyShareLink}
                  className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-white/5 hover:bg-white/10 text-neutral-200 text-sm font-semibold border border-white/10 transition"
                  data-testid="dish-detail-share-copy">
                  <LinkIcon size={16} /> {shareCopied ? 'Copied!' : 'Copy link'}
                </button>
              </div>
              {shareLink && (
                <p className="text-[11px] text-neutral-500 break-all" data-testid="dish-detail-share-link">{shareLink}</p>
              )}
              <p className="text-[11px] text-neutral-500">
                The cook receives a no-login page on his phone with the recipe, video, step photos and ingredients —
                in {window.localStorage.getItem('mealExportPrefs.v1')?.includes('"hi"') ? 'हिन्दी' : 'English'} (matches your kitchen language). Tap लोगो at top of that page to flip language anytime.
              </p>

              <div className="pt-2 flex flex-wrap gap-2">
                <button onClick={refreshRecipe} disabled={busy === 'refresh'}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 disabled:opacity-50 text-neutral-300 text-xs font-semibold border border-white/10 transition"
                  data-testid="dish-detail-refresh">
                  {busy === 'refresh' ? <Spinner size={14} className="animate-spin" /> : <ArrowsClockwise size={14} />}
                  Generate alternative recipe with AI
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
